import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';

import { svgCourseBg } from '@components/learning/LearningSidebar/assets/courseBg';
import { svgLearningPathBg } from '@components/learning/LearningSidebar/assets/learningPath';
import { media } from '@atlaskit/primitives';

interface HeaderProps {
  isCourse: boolean;
  learningPathSlug?: string;
  courseSlug?: string;
  completedItems: string;
  courseImgUrl?: string;
  title: string;
  progress: number;
  navigate: (path: string) => void;
}

/**
 * Props for the Header component
 * @param {boolean} isCourse - Indicates whether this is a course or learning path
 * @param {string} completedItems - Text showing completed items (e.g., "3/5 completed")
 * @param {string} [courseImgUrl] - Optional URL for the course image
 * @param {string} title - Title of the course or learning path
 * @param {number} progress - Progress percentage (0-100)
 * @param {string} [learningPathSlug] - Optional slug of the learning path to be used for the header URL
 * @param {() => void} navigate - Function to navigate to a given path
 * @param {string} [courseSlug] - Optional slug of the course to be used for the header URL
 */

export const Header: React.FC<HeaderProps> = ({
  isCourse,
  courseImgUrl,
  title,
  completedItems,
  progress,
  learningPathSlug,
  courseSlug,
  navigate,
}) => (
  <div data-testid="sidebar-header" css={headerStyles}>
    <img css={headerImageStyles} src={isCourse ? svgCourseBg : svgLearningPathBg} alt={title} />
    <div css={[headerContentStyles, isCourse && courseHeaderContentStyles]}>
      {isCourse && (
        <div css={courseImageContainerStyles}>
          <img css={courseImageStyles} src={courseImgUrl} alt={title} data-testid="course-image" />
        </div>
      )}
      <div css={[containerStyles, isCourse && css({ marginLeft: token('space.150') })]}>
        <p css={typeStyles}>{isCourse ? 'Course' : 'Learning Path'}</p>
        <p
          css={titleStyles}
          data-testid="sidebar-title"
          onClick={(): void =>
            learningPathSlug ? navigate(`/path/${learningPathSlug}`) : navigate(`/course/${courseSlug}`)
          }
        >
          {title}
        </p>
      </div>
      <div data-testid="course-progress-circle" css={progressCircleContainerStyles}>
        <CircularProgressbarWithChildren
          strokeWidth={8}
          value={progress}
          styles={buildStyles({
            pathColor: token('color.border.success'),
            trailColor: 'rgba(9, 30, 66, 0.14)',
          })}
        >
          <p css={[progressStyles, isCourse && css({ fontSize: '14px' })]} data-testid="progress-text">
            {completedItems}
          </p>
        </CircularProgressbarWithChildren>
      </div>
    </div>
  </div>
);

const headerStyles = css({
  width: '100%',
  marginBottom: token('space.200'),
});

const headerImageStyles = css({
  [media.below.xs]: {
    width: '100%',
  },
});

const headerContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: token('space.250'),
  paddingLeft: token('space.200'),
  paddingRight: token('space.100'),
});

const courseHeaderContentStyles = css({
  paddingLeft: token('space.150'),
  paddingRight: token('space.150'),
});

const progressCircleContainerStyles = css({
  maxWidth: '48px',
  marginLeft: token('space.150'),
});

const progressStyles = css({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
});

const courseImageContainerStyles = css({
  width: '32px',
  minWidth: '32px',
  minHeight: '32px',
});

const courseImageStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  marginRight: 'auto',
});

const typeStyles = css({
  color: token('color.text.subtle'),
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '12px',
  lineHeight: '16px',
  fontWeight: 600,
  textTransform: 'uppercase',
  marginBottom: token('space.050'),
});

const titleStyles = css({
  fontFamily: 'Charlie Display, sans-serif',
  fontSize: '20px',
  fontWeight: 600,
  lineHeight: '28px',
  marginTop: 0,
  transition: 'color 0.5s ease',

  [media.below.xs]: {
    fontSize: '16px',
    lineHeight: '24px',
  },

  '&:hover': {
    cursor: 'pointer',
    color: token('color.text.selected'),
  },
});
